import React, { useState } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { payrollAmountInputProps } from "./payrollAmountInput";
import { getPayrollComponentDefaults } from "./payrollComponentDefaults";
import dayjs from "dayjs";

const EMPLOYEE_VALUE_KINDS = ["Earning", "Employee Deduction"];

const isEmployeeValueComponent = (item) =>
  item.isActive &&
  item.allowEmployeeOverride &&
  EMPLOYEE_VALUE_KINDS.includes(item.kind);

const PayrollEmployeeOverridesModal = ({
  employee,
  components,
  overrides,
  saving,
  onClose,
  saveOverride,
  deleteOverride,
  inline = false,
}) => {
  const [form] = Form.useForm();
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const componentId = Form.useWatch("componentId", form);
  const component = components.find((item) => item.componentId === componentId);
  const usePercentage = component?.calculationMethod?.startsWith("Percentage");

  const showForm = (row = null) => {
    setSelected(row);
    form.resetFields();
    form.setFieldsValue(
      row
        ? {
            ...row,
            effectiveFrom: dayjs(row.effectiveFrom),
            effectiveTo: row.effectiveTo ? dayjs(row.effectiveTo) : null,
            amount: row.amount == null ? null : Number(row.amount),
            percentage: row.percentage == null ? null : Number(row.percentage),
          }
        : {
            profileId: employee.profileId,
            effectiveFrom: dayjs(),
            isActive: true,
          },
    );
    setFormOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();
    const result = await saveOverride(selected?.overrideId, {
      ...values,
      profileId: employee.profileId,
      amount: usePercentage ? null : values.amount,
      percentage: usePercentage ? values.percentage : null,
      effectiveFrom: values.effectiveFrom.format("YYYY-MM-DD"),
      effectiveTo: values.effectiveTo?.format("YYYY-MM-DD") || null,
    });
    if (result) setFormOpen(false);
  };

  const columns = [
    {
      title: "Component",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <span>{row.componentName}</span>
          <span style={{ color: "#8c8c8c" }}>{row.componentCode}</span>
        </Space>
      ),
    },
    { title: "Type", dataIndex: "componentKind" },
    {
      title: "Amount",
      render: (_, row) =>
        row.amount != null
          ? `UGX ${Number(row.amount).toLocaleString()}`
          : `${Number(row.percentage)}%`,
    },
    {
      title: "Effective",
      render: (_, row) =>
        `${dayjs(row.effectiveFrom).format("DD MMM YYYY")} — ${row.effectiveTo ? dayjs(row.effectiveTo).format("DD MMM YYYY") : "Open"}`,
    },
    { title: "Reason", dataIndex: "reason" },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (value) => (
        <Tag color={value ? "green" : "default"}>
          {value ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      width: 90,
      align: "center",
      render: (_, row) => (
        <Space size={0}>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => showForm(row)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this employee value?"
            onConfirm={() => deleteOverride(row.overrideId, employee.profileId)}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const content = (
    <Space direction="vertical" style={{ width: "100%" }}>
      <div style={{ textAlign: "right" }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => showForm()}
        >
          Add earning / deduction
        </Button>
      </div>
      <Table
        rowKey="overrideId"
        dataSource={overrides}
        columns={columns}
        pagination={false}
      />
    </Space>
  );

  return (
    <>
      {inline ? (
        content
      ) : (
        <Modal
          title={`Employee earnings & deductions — ${employee?.staffName || ""}`}
          open={Boolean(employee)}
          onCancel={onClose}
          footer={null}
          width={920}
        >
          {content}
        </Modal>
      )}
      <Modal
        title={
          selected
            ? "Edit employee earning or deduction"
            : "Add employee earning or deduction"
        }
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        onOk={submit}
        confirmLoading={saving}
        okText="Save value"
        zIndex={inline ? 1200 : undefined}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="componentId"
            label="Earning or deduction"
            rules={[{ required: true }]}
          >
            <Select
              disabled={Boolean(selected)}
              showSearch
              optionFilterProp="label"
              notFoundContent="Create an active employee-overridable earning or deduction in Payroll Setup."
              options={components
                .filter(
                  (item) =>
                    isEmployeeValueComponent(item) ||
                    item.componentId === selected?.componentId,
                )
                .map((item) => ({
                  value: item.componentId,
                  label: `${item.name} — ${item.kind} (${item.code} v${item.versionNumber})`,
                }))}
              onChange={(selectedComponentId) => {
                const selectedComponent = components.find(
                  (item) => item.componentId === selectedComponentId,
                );
                form.setFieldsValue(
                  getPayrollComponentDefaults(selectedComponent),
                );
              }}
            />
          </Form.Item>
          {usePercentage ? (
            <Form.Item
              name="percentage"
              label="Employee percentage"
              rules={[{ required: true }]}
              extra={
                selected
                  ? undefined
                  : "The configured default is prefilled and can be changed for this employee."
              }
            >
              <InputNumber
                min={0}
                max={100}
                precision={4}
                addonAfter="%"
                style={{ width: "100%" }}
              />
            </Form.Item>
          ) : (
            <Form.Item
              name="amount"
              label="Employee amount (UGX)"
              rules={[{ required: true }]}
              extra={
                selected
                  ? undefined
                  : "The configured default is prefilled and can be changed for this employee."
              }
            >
              <InputNumber
                {...payrollAmountInputProps}
                min={0}
                precision={2}
                style={{ width: "100%" }}
              />
            </Form.Item>
          )}
          <Space align="start">
            <Form.Item
              name="effectiveFrom"
              label="Effective from"
              rules={[{ required: true }]}
            >
              <DatePicker />
            </Form.Item>
            <Form.Item name="effectiveTo" label="Effective to">
              <DatePicker />
            </Form.Item>
            <Form.Item name="isActive" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, whitespace: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PayrollEmployeeOverridesModal;
